import { TaskActionTimingSchema } from '../guidance/lib/task-action-events'
// Server state for ONE task action's full body (/api/task-action/:id) — the provenance node a reader
// expands on the task page (see components/TaskActionsSection).
//
// Only the detail read lives here: the task page's action LIST arrives inside the task-detail envelope
// (metadata only, no payloads), and a payload is fetched per action on demand. Keyed by action id, so
// re-expanding a row — or following a dependency link back to one already opened — is served from cache.
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { z } from 'zod'
import { JourneyChronologyResponseSchema } from '../guidance/journey/chronology'
import { apiFetch } from '../lib/apiFetch'

// A neighbour in the graph. `task_id`/`kind` are null when the edge points at an action whose row is gone
// (pruned, or a write that never landed) — the UI shows that as a dangling reference rather than hiding it.
export const TaskActionNeighbourSchema = z.object({
  id: z.string(),
  task_id: z.string().nullable(),
  kind: z.string().nullable(),
  label: z.string().nullable(),
  status: z.string().nullable(),
})

// The outcome of locating a task's actions in its driver recording (POST /api/task-action/resolve-points).
// `resolved` is how many rows were stamped; `reason` says why none were (no driver recording, no markers
// yet, the read failed) — the cases a reader otherwise reads as "the button does nothing".
export const ResolvePointsResultSchema = z.object({
  recordingId: z.string().nullable(),
  resolved: z.number(),
  reason: z.string().optional(),
})

export const TaskActionDetailSchema = z.object({
  timing_events: z.record(TaskActionTimingSchema).default({}),
  id: z.string(),
  task_id: z.string(),
  seq: z.number(),
  kind: z.string(),
  label: z.string().nullable(),
  status: z.string(),
  // Where this action happened in the driver recording, once resolved from its console marker.
  recording_id: z.string().nullable(),
  recording_point: z.string().nullable(),
  recording_time_ms: z.number().nullable(),
  // Whatever the action produced — opaque by design, rendered as JSON.
  data: z.unknown().nullable(),
  meta: z.record(z.unknown()).nullable(),
  llm_job_ids: z.array(z.string()).nullable(),
  error: z.string().nullable(),
  started_at: z.string().nullable(),
  ended_at: z.string().nullable(),
  created_at: z.string(),
  depends_on: z.array(TaskActionNeighbourSchema),
  consumed_by: z.array(TaskActionNeighbourSchema),
})

export const TaskActionResponseSchema = z.object({ action: TaskActionDetailSchema })

export type TaskActionNeighbour = z.infer<typeof TaskActionNeighbourSchema>
export type TaskActionDetail = z.infer<typeof TaskActionDetailSchema>
export type ResolvePointsResult = z.infer<typeof ResolvePointsResultSchema>

export const BugJudgeActionsSchema = z.object({ actions: z.array(z.object({
  stage: z.string(), label: z.string(), action_id: z.string(), task_id: z.string(), project_id: z.string(),
})) })

export const taskActionsApi = createApi({
  reducerPath: 'taskActionsApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/', fetchFn: (input, init) => apiFetch(input, init) }),
  endpoints: (builder) => ({
    getBugJudgeActions: builder.query<z.infer<typeof BugJudgeActionsSchema>, string>({
      query: (bugId) => ({ url: '/api/task-action/bug-judges', params: { bug_id: bugId } }),
      transformResponse: (response) => BugJudgeActionsSchema.parse(response),
    }),
    // The active state is part of the cache key so reaching a terminal run fetches its final entries.
    getJourneyChronology: builder.query<z.infer<typeof JourneyChronologyResponseSchema>, { taskId: string; active: boolean }>({
      query: ({ taskId }) => ({ url: '/api/task-action/chronology', params: { task_id: taskId } }),
      transformResponse: (response) => JourneyChronologyResponseSchema.parse(response),
    }),
    getTaskAction: builder.query<TaskActionDetail, string>({
      query: (actionId) => ({ url: `/api/task-action/${actionId}` }),
      transformResponse: (response) => TaskActionResponseSchema.parse(response).action,
    }),

    // Locate every action of a task in its driver recording. The points are cached on the rows, so the
    // task page picks them up on its next envelope refresh.
    resolveTaskActionPoints: builder.mutation<ResolvePointsResult, string>({
      query: (taskId) => ({ url: '/api/task-action/resolve-points', method: 'POST', body: { task_id: taskId } }),
      transformResponse: (response) => ResolvePointsResultSchema.parse(response),
    }),
  }),
})

export const { useGetBugJudgeActionsQuery, useGetJourneyChronologyQuery, useGetTaskActionQuery, useResolveTaskActionPointsMutation } = taskActionsApi
